/**
 * Host parsing. Pure functions, shared by client + server and unit-tested.
 *
 * The SPA is served from ONE Cloudflare Pages project on every attached host:
 * the apex/app host runs the dashboard; a wildcard `*.<apex>` fronts each
 * seller store's public storefront. Which mode a request is in is decided purely
 * from its hostname, compared against the configured platform apex.
 */
import { RESERVED_SUBDOMAINS } from "./slug";

export interface HostInfo {
  /** The store subdomain label, e.g. "ubai" for ubai.digital-embed.com — or null
   *  when the host is the app itself (apex, www, app, or an unrelated host). */
  subdomain: string | null;
}

/** Strip a port and lowercase a hostname. */
function normalizeHost(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, "").split(":")[0] ?? "";
}

/**
 * Resolve a hostname against the platform apex (e.g. "digital-embed.com").
 *
 *   digital-embed.com          -> { subdomain: null }   (app)
 *   www.digital-embed.com      -> { subdomain: null }   (app; www is reserved)
 *   app.digital-embed.com      -> { subdomain: null }   (app; app is reserved)
 *   ubai.digital-embed.com     -> { subdomain: "ubai" } (storefront)
 *   ubai.digital-embed.test    -> { subdomain: "ubai" } (local Herd, apex=.test)
 *   localhost / 127.0.0.1 / IP -> { subdomain: null }   (bare local dev)
 *
 * A multi-label subdomain (a.b.digital-embed.com) is treated as app, not a
 * store — stores are exactly one label deep. Reserved labels resolve to app so
 * infra hosts never masquerade as a storefront.
 */
export function parseHost(hostname: string, apex: string): HostInfo {
  const host = normalizeHost(hostname);
  const root = normalizeHost(apex);
  if (!host || !root) return { subdomain: null };
  if (host === root) return { subdomain: null };

  const suffix = `.${root}`;
  if (!host.endsWith(suffix)) return { subdomain: null };

  const label = host.slice(0, -suffix.length);
  // Exactly one label deep, valid, and not reserved.
  if (!label || label.includes(".")) return { subdomain: null };
  if (RESERVED_SUBDOMAINS.has(label)) return { subdomain: null };
  return { subdomain: label };
}

/**
 * How the SPA should behave for a hostname:
 *   - "app": the dashboard/admin/marketing (apex, www/app, or a local dev host)
 *   - "subdomain": a platform-subdomain storefront (<label>.<apex>)
 *   - "domain": a seller's OWN custom-domain storefront (a foreign hostname)
 */
export type HostMode =
  | { kind: "app" }
  | { kind: "subdomain"; subdomain: string }
  | { kind: "domain"; host: string };

/** True for hosts that are always the app in local dev (never a custom domain). */
function isLocalHost(host: string): boolean {
  return (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "[::1]" ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host) // bare IPv4
  );
}

/**
 * Classify a hostname against the platform apex. An apex-subdomain resolves to a
 * subdomain storefront; a foreign, non-local hostname is treated as a custom
 * domain (the server confirms it maps to a verified store, else 404). The apex
 * itself, reserved labels, and local hosts are the app.
 *
 * `alsoServed` lists retired apexes this build must still recognise. Without it a
 * domain move breaks every storefront on the old apex the moment the new one is
 * configured: `kedai.<retired>` would fall through to "domain", the server would
 * look it up in `stores.custom_domain`, find nothing, and 404. Passing the old
 * apex here keeps those hosts serving in place, which is what makes the retired-
 * apex bridge OPTIONAL — and therefore reversible — rather than load-bearing.
 */
export function hostMode(hostname: string, apex: string, alsoServed: string[] = []): HostMode {
  const host = normalizeHost(hostname);
  if (!host) return { kind: "app" };

  for (const candidate of [apex, ...alsoServed]) {
    const root = normalizeHost(candidate);
    if (!root) continue;
    if (host === root || host.endsWith(`.${root}`)) {
      const { subdomain } = parseHost(host, root);
      return subdomain ? { kind: "subdomain", subdomain } : { kind: "app" };
    }
  }

  if (isLocalHost(host)) return { kind: "app" };
  return { kind: "domain", host };
}

/**
 * The apex URL a buyer auth/membership route should bounce to when it lands on a
 * seller storefront host, or null when it should stay in place.
 *
 * Buyer login, password reset, and the membership area MUST run on the apex:
 *   - Cloudflare Turnstile only authorises the apex hostname; on a seller
 *     subdomain or custom domain the widget errors 110200 ("domain not
 *     authorized") and login is impossible.
 *   - Supabase sessions are per-origin (localStorage), so a session created on a
 *     seller host is invisible to the apex membership area anyway.
 *
 * Returns null for the app/apex itself and for local dev hosts (so `?__store=`
 * overrides keep working in place); returns `https://<apex><path>` otherwise.
 * Storefront hosts are always https in production, so the scheme is fixed.
 */
export function apexBuyerRedirect(
  hostname: string,
  apex: string,
  path: string,
): string | null {
  const root = normalizeHost(apex);
  if (!root) return null;
  if (hostMode(hostname, root).kind === "app") return null;
  return `https://${root}${path}`;
}

/** The apex from a full URL/origin, e.g. "http://digital-embed.test" -> "digital-embed.test". */
export function apexFromUrl(url: string): string {
  try {
    return normalizeHost(new URL(url).hostname);
  } catch {
    return normalizeHost(url);
  }
}

/**
 * The host on the CURRENT apex that a request to a retired apex should land on,
 * or null when `hostname` has nothing to do with the retired apex.
 *
 *   sellfy.win           -> sellhiz.win          (apex to apex)
 *   www.sellfy.win       -> sellhiz.win          (www is never a store; one hop, not two)
 *   ubai.sellfy.win      -> ubai.sellhiz.win     (store keeps its label)
 *   a.b.sellfy.win       -> a.b.sellhiz.win      (whatever depth, preserved)
 *   kedaisaya.com        -> null                 (a seller's own domain is not ours to move)
 *
 * The store label is carried across verbatim: a seller who printed
 * `kedai.sellfy.win` on a flyer keeps landing on their own storefront.
 */
export function legacyHostTwin(
  hostname: string,
  legacyApex: string,
  apex: string,
): string | null {
  const host = normalizeHost(hostname);
  const legacy = normalizeHost(legacyApex);
  const root = normalizeHost(apex);
  if (!host || !legacy || !root || legacy === root) return null;
  if (host === legacy) return root;
  if (!host.endsWith(`.${legacy}`)) return null;
  const label = host.slice(0, -(legacy.length + 1));
  if (!label || label === "www") return root;
  return `${label}.${root}`;
}

const BUYER_PAGE = /^\/(account|access\/[\w-]+|learn\/[\w-]+(\/certificate)?|account\/(certificate|ebook)\/[\w-]+)$/;

/**
 * Where an emailed sign-in link may land a buyer. The path is appended to the
 * apex and handed to Supabase as the link's redirect, so it is held to the
 * buyer's own pages; anything else — another host, a query string, the login
 * page itself — lands on the purchases hub instead.
 */
export function buyerLandingPath(raw: string | null | undefined): string {
  return raw && BUYER_PAGE.test(raw) ? raw : "/account";
}
